import { createSlice } from '@reduxjs/toolkit';
import type { ThunkDispatch, UnknownAction } from '@reduxjs/toolkit';
import type { LookupItemGuid } from '../../types/common/common.ts';
import type { ProductLookupOption } from '../../services/repositories/lookupsService.ts';
import { emptyRequest } from '../requests.ts';
import type { RequestState } from '../requests.ts';
import { fetchLookup, fetchProductOptions, lookupRequestKey, productLookupKey } from '../thunks/lookupsThunks.ts';

export interface LookupsState {
  lists: Record<string, RequestState<LookupItemGuid[]>>;
  products: Record<string, RequestState<ProductLookupOption[]>>;
}

export interface LookupsRootState {
  lookups: LookupsState;
}

export type LookupsDispatch = ThunkDispatch<LookupsRootState, unknown, UnknownAction>;

const initialState: LookupsState = { lists: {}, products: {} };

const lookupsSlice = createSlice({
  name: 'lookups',
  initialState,
  reducers: {
    resetLookups: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchLookup.pending, (state, action) => {
        const key = lookupRequestKey(action.meta.arg);
        state.lists[key] = {
          ...(state.lists[key] ?? emptyRequest),
          status: 'pending', requestId: action.meta.requestId, error: undefined,
        };
      })
      .addCase(fetchLookup.fulfilled, (state, action) => {
        const request = state.lists[lookupRequestKey(action.meta.arg)];
        if (request?.requestId !== action.meta.requestId) return;
        request.data = action.payload;
        request.status = 'fulfilled';
        request.updatedAt = Date.now();
        request.requestId = undefined;
      })
      .addCase(fetchLookup.rejected, (state, action) => {
        const request = state.lists[lookupRequestKey(action.meta.arg)];
        if (request?.requestId !== action.meta.requestId) return;
        request.status = action.meta.aborted ? (request.data ? 'fulfilled' : 'idle') : 'rejected';
        request.error = action.meta.aborted ? undefined : action.error;
        request.requestId = undefined;
      })
      .addCase(fetchProductOptions.pending, (state, action) => {
        const key = productLookupKey(action.meta.arg);
        state.products[key] = {
          ...(state.products[key] ?? emptyRequest),
          status: 'pending', requestId: action.meta.requestId, error: undefined,
        };
      })
      .addCase(fetchProductOptions.fulfilled, (state, action) => {
        const request = state.products[productLookupKey(action.meta.arg)];
        if (request?.requestId !== action.meta.requestId) return;
        request.data = action.payload;
        request.status = 'fulfilled';
        request.updatedAt = Date.now();
        request.requestId = undefined;
      })
      .addCase(fetchProductOptions.rejected, (state, action) => {
        const request = state.products[productLookupKey(action.meta.arg)];
        if (request?.requestId !== action.meta.requestId) return;
        request.status = action.meta.aborted ? (request.data ? 'fulfilled' : 'idle') : 'rejected';
        request.error = action.meta.aborted ? undefined : action.error;
        request.requestId = undefined;
      });
  },
});

export const { resetLookups } = lookupsSlice.actions;
export const lookupsReducer = lookupsSlice.reducer;
export default lookupsReducer;